import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Card} from "react-bootstrap";
import {DatePicker, LocalizationProvider, MobileDatePicker, TimePicker, DesktopTimePicker} from '@mui/x-date-pickers';
import {translateService} from "../../services/TranslateService";
import moment from "moment";
import {enUS, fiFI} from '@mui/x-date-pickers/locales';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/fi';
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {useSelector} from "react-redux";
import {GlobalStore} from "../../reduxStore";
import styles from "./VehicleSearch.module.scss";
import {DataContext, ISearchParameters} from "../../contexts/dataContext";
import Select from 'react-select'
import {Dialog} from "@mui/material";
import {Check24rCookie, GetCustomerBehaviourParams} from "../../services/CustomerBehaviourData";
import Translate from "react-translate-component";
import Tippy from "@tippyjs/react";
import Autocomplete from "react-google-autocomplete";
import SvgIcon from '@mui/material/SvgIcon';

const datePickerTheme = createTheme({
    palette: {
        primary: {
            main: '#fbb040'
        },
    },
});

declare type SearchProps = {
    setSearchParams: Function;
    setErrorMsg: Function;
    clearResults: Function;
    modifyReservation?: boolean;
    scrollRef?: React.RefObject<null | HTMLElement>;
}

const VehicleSearch = (props: SearchProps) => {
    const {user, searchParameters, changeSearchParameters, oneWayRoutes, oneWayRes,
        changeResType, enableOneWay, changeMapData, mapData, sendGaEvent, showDefaultResults,
        changeShowDefaultResults, loggedIn, changeFilteredList, mobileDevice, rootUrl, appContext} = useContext(DataContext)
    const language = useSelector((state: GlobalStore) => state.chauffeurReducer.deviceLanguage);
    const [isAddressSelected, setIsAddressSelected] = useState(!!searchParameters?.location);
    const startDate = moment(searchParameters?.startDate).seconds(0).milliseconds(0).clone()
    const endDate = searchParameters?.endDate ? moment(searchParameters?.endDate).seconds(0).milliseconds(0).clone() :
        moment().seconds(0).milliseconds(0).add(2, 'hours'); // if redirected from created res payment screen --> searchParameters.endDate will be undefined, hence using current time + 2 hours
    const defaultPickUpCity = searchParameters?.pickupCity;
    const defaultReturnCity = searchParameters?.returnCity;
    const [pickupDate, setPickupDate] = useState<moment.Moment>(startDate);
    const [returnDate, setReturnDate] = useState<moment.Moment>(endDate);
    const [returnError, setReturnError] = useState<string | null>(null);
    const [pickupError, setPickupError] = useState<string | null>(null);
    const [location, setLocation] = useState<any>(searchParameters?.location);
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const API_VERSION = useSelector((state: GlobalStore) => state.chauffeurReducer.API_VERSION);
    const [isOneWayRes, setIsOneWayRes] = useState<boolean | undefined>(oneWayRes);
    const [oneWaySelectOptionsPickup, setOneWaySelectOptionsPickup] = useState<any>([]);
    const [oneWaySelectOptionsReturn, setOneWaySelectOptionsReturn] = useState<any>([]);
    const [pickupCityChangeByDD, setPickupCityChangeByDD] = useState<boolean>(false);
    const [oneWayPickupCity, setOneWayPickupCity] = useState<{ value: string, label: string, isDisabled: boolean }>(defaultPickUpCity ? {value: defaultPickUpCity, label: defaultPickUpCity, isDisabled: true} :
        {value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true}); // works with react-select
    const [oneWayReturnCity, setOneWayReturnCity] = useState<{ value: string, label: string, isDisabled: boolean }>(defaultReturnCity ? {value: defaultReturnCity, label: defaultReturnCity, isDisabled: true} :
        {value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true});
    const PopulateReturnCityList = useCallback((oneWayRoutesArray: any, pickupCityValue: string) => {
        // setting corresponding return cities for selected pickup city
        let returnCities = [{value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true}];
        let returnCitiesList = oneWayRoutesArray[pickupCityValue];
        returnCitiesList.sort();
        for (let j=0; j < returnCitiesList.length; j++) {
            returnCities.push({value: oneWayRoutesArray[pickupCityValue][j],
                label: oneWayRoutesArray[pickupCityValue][j], isDisabled: false});
        }
        setOneWaySelectOptionsReturn(returnCities);
    }, []);
    const r24CookieVal = Check24rCookie();
    const [isLocationEmpty, setIsLocationEmpty] = useState(true);
    const customerBehaviorData = GetCustomerBehaviourParams();
    const [isPickupDatePickerOpen, setPickupDatePickerOpen] = useState(false);
    const [isPickupTimePickerOpen, setPickupTimePickerOpen] = useState(false);
    const [isReturnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
    const [isReturnTimePickerOpen, setReturnTimePickerOpen] = useState(false);
    const [selectedALL, setSelectedAll] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [hasPopupShown, setHasPopupShown] = useState(false);
    const [lat, setLat] = useState<number | null>(searchParameters?.lat || null);
    const [lng, setLng] = useState<number | null>(searchParameters?.lon || null);
    const autoCompleteRef = useRef<HTMLInputElement>(null); // used to control search input field when geocoded value not selected + search clicked
    const [userLocationSearchString, setUserLocationSearchString] = useState('');

    const handleOneWayCityChange = useCallback((city: any, selectType: string) => {
        if(selectType === 'pickup') {
            setOneWayPickupCity(city);
            setPickupCityChangeByDD(true);
        } else { setOneWayReturnCity(city); }
    }, []);

    const handleSearchChanges = () => {
        if (showDefaultResults) {
            // removes default results view if location search changes
            changeShowDefaultResults(false);
            changeMapData([]);
        }
    }

    const getDefaultLocationValue = (): string => {
        if(searchParameters?.location) {
            if(searchParameters?.location?.formatted_address) {
                return searchParameters?.location?.formatted_address;
            } else {
                return searchParameters.location;
            }
        }
        return '';
    }

    useEffect(() => {
        if(mapData) {
            changeFilteredList(mapData);
        }
    }, [mapData]);

    useEffect(() => {
        // if one way res set from url params - this useEffect allows isOneWayRes state to be updated after initial state declaration
        if(isOneWayRes !== oneWayRes) {
            setIsOneWayRes(oneWayRes);
        }
    }, [oneWayRes]);

    useEffect(() => {
        props.clearResults();
    }, [location, pickupDate, returnDate, oneWayPickupCity, oneWayReturnCity]);

    useEffect(() => {
        setPickupDate(startDate)
        setReturnDate(endDate)
        if (searchParameters?.location) {
            setLocation(searchParameters?.location);
            setLat(searchParameters?.lat);
            setLng(searchParameters?.lon);
            if (!isAddressSelected) {
                setIsAddressSelected(!!searchParameters?.location);
            }
        }
    }, [searchParameters]);

    useEffect(() => {
        // effects from changing between OneWay & a2a type reservations
        // clears previous one-way city selections
        if (!isOneWayRes) { // on resets on checkbox change, not on page reload
            setOneWayPickupCity({ value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true });
            setOneWayReturnCity({ value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true });
        }
    }, [isOneWayRes]);

    useEffect(() => {
        setIsLocationEmpty(!location);
        let locationFromUrl = sessionStorage.getItem('locationFromUrl');
        if (location && !searchParameters?.cookie && !selectedALL && !locationFromUrl && !searchParameters?.location) {
            setPickupDatePickerOpen(true);
        }

        if (locationFromUrl === '1') { // only needed once
            setHasPopupShown(true);
            setSelectedAll(true);
            sessionStorage.removeItem('locationFromUrl');
        }
    }, [location]);

    useEffect(() => {
        // sets city data for one way pick up selections
        if (Object.keys(oneWayRoutes).length > 0) {
            let pickUpCities = Object.keys(oneWayRoutes);
            pickUpCities.sort(); // to alphabetical order
            let pickUpCitiesArray = [{value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true}];
            for(let i=0; i < pickUpCities.length; i++) {
                pickUpCitiesArray.push({value: pickUpCities[i], label: pickUpCities[i], isDisabled: false});
            }
            setOneWaySelectOptionsPickup(pickUpCitiesArray);

            // check if last saved pickup location is valid (case for reload/refresh/onBack page)
            if(oneWayPickupCity.value.length > 0) {
                if(!Object.keys(oneWayRoutes).includes(oneWayPickupCity.value)){
                    setOneWayPickupCity({value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true});
                    setOneWayReturnCity({value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true});
                } else {
                    PopulateReturnCityList(oneWayRoutes, oneWayPickupCity.value);
                }
            }
        }
    }, [oneWayRoutes, PopulateReturnCityList]);

    useEffect(() => {
        // effects from changing one way pick up city selection
        if (Object.keys(oneWayRoutes).length > 0 && (oneWayPickupCity.value).length > 0) {
            PopulateReturnCityList(oneWayRoutes, oneWayPickupCity.value);
            if (pickupCityChangeByDD) {
                setOneWayReturnCity({value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true});
                setPickupCityChangeByDD(false);
            }
        }
    }, [oneWayPickupCity, pickupCityChangeByDD, oneWayRoutes, PopulateReturnCityList]);

    const pickupErrorMessage = React.useMemo(() => {
        switch (pickupError) {
            case "minDateTime":
            case "minTime":
            case "minDate":
                setDisableButton(true);
                return (translateService.translate('vehicleSearch.pickupTimeAtLeast'));

            default:
                setDisableButton(false);
                return null;
        }}, [pickupError]
    );

    const returnErrorMessage = React.useMemo(() => {
            switch (returnError) {
                case "minTime":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.returnTimeAtLeast'));

                case "maxDate":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.returnDateCannotAfter'));

                case "minDate":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.returnDateCannotBefore'));

                case "minDateTime":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.pickupTimeAtLeast'));

                default:
                    setDisableButton(false);
                    return null
            }
        }, [returnError]
    );

    //current time is still wrong
    //spill over to next hour if minimum time is over 45 min, because of 15 minute time-step
    let minimumTime = moment().add(15, "minutes");
    if (minimumTime.minute() > 45) {
        minimumTime.hours(minimumTime.hour() + 1).minutes(0).seconds(0);
    }

    // Modify the search box styling based on the state variable
    let searchboxEl = document.getElementById("google-search-location")
    if (searchboxEl) {
        searchboxEl?.classList.add('searchbox-input')

        let labelInput = document.getElementById('searchbox-label')

        if (searchboxEl && labelInput) {
            if (isLocationEmpty) {
                // Apply red border and yellow background color when the location search box is empty
                searchboxEl.style.border = '1px solid #F53232';
                searchboxEl.style.backgroundColor = '#fff8dd';
                labelInput.style.color = '#F53232';
            } else {
                // Revert the border and background color when the location is selected
                searchboxEl.style.border = '1px solid #ccc';
                searchboxEl.style.backgroundColor = '#fff';
                labelInput.style.color = 'rgba(0, 0, 0, 0.6)';
            }

            searchboxEl.addEventListener('click', () => {
                if (searchboxEl && labelInput && !isLocationEmpty) {
                    searchboxEl.style.border = '2px solid #fbb040'
                    labelInput.style.color = '#fbb040'
                }
            })

            // back to default color
            searchboxEl.addEventListener('blur', () => {
                if (searchboxEl && labelInput) {
                    labelInput.style.color = 'rgba(0, 0, 0, 0.6)';
                    // Check if the input is empty
                    if(isLocationEmpty) {
                        // If it's empty, apply the red border and yellow background color
                        searchboxEl.style.border = '1px solid #F53232';
                        searchboxEl.style.backgroundColor = '#fff8dd';
                        labelInput.style.color = '#F53232';
                    } else {
                        // If it's not empty, revert the border color
                        searchboxEl.style.border = '1px solid #ccc';
                        searchboxEl.style.backgroundColor = '#fff';
                        labelInput.style.color = 'rgba(0, 0, 0, 0.6)';
                    }
                }
            });
        }
    }

    const checkDateTimeValues = (startDateToCheck: any, endDateToCheck: any) => {
        // ensure search date time values are in future + min res length is 2h
        let validDateTimes = true;
        if (startDateToCheck < moment()) {
            setPickupError("minDateTime");
            validDateTimes = false;
        }

        if (endDateToCheck < moment()) {
            setReturnError("minDateTime");
            validDateTimes = false;
        } else if (endDateToCheck.isBefore(moment(startDateToCheck).seconds(0).milliseconds(0).add(2, "hours"))) {
            setReturnError("minTime")
            validDateTimes = false;
        }

        return validDateTimes;
    }

    function MuiDateTimeIcons() {
        return ( // empty icon to make mui default invisible
            <SvgIcon></SvgIcon>
        );
    }

    return (
        <>
            {props.modifyReservation ?
                <h2 className="past-reservation-title my-3 text-center">
                    {translateService.translate('vehicleSearch.services.changeDates')}
                </h2> : null}

            <Card style={{padding: "10px"}}>
                { enableOneWay && !props.modifyReservation ?
                    <div className={styles.onewayCheckboxDiv}>
                        <Tippy
                            className="tooltip-link"
                            zIndex={100}
                            content={<Translate content={'vehicleSearch.onewayTip'} />}
                            placement={"bottom"}
                        >
                            <img width="23" height="23" className={styles.carFeatureIcon} src={rootUrl+"/assets/icons/new/search/icon-info-oneway-green.svg"} alt="icon-info-oneway-green" loading="lazy"/>
                        </Tippy>
                        <label className={styles.onewayCheckboxLabel}> {translateService.translate('vehicleSearch.returnToDifferentCity')} </label>
                        <input className={styles.r24CheckboxInput}
                               type={"checkbox"} checked={isOneWayRes}
                               onChange={() => {
                                   props.clearResults();
                                   !oneWayRes ? sendGaEvent('searchOneWayCars', 'oneWaySelected', '', 0) : sendGaEvent('searchOneWayCars', 'oneWayUnSelected', '', 0);
                                   setIsOneWayRes(!isOneWayRes);
                                   changeMapData([]);
                                   changeResType(!oneWayRes);
                               }}/>

                    </div> : null
                }
                {isOneWayRes ?
                    <form>
                        {!props.modifyReservation &&
                            <div className={styles.onewaySearchDropdownContainer}>
                                <div className={styles.onewayPickupDropdown}>
                                    <Select options={oneWaySelectOptionsPickup}
                                            onChange={(e) => {handleOneWayCityChange(e, 'pickup')}}
                                            value={oneWayPickupCity.value === '' ? {value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true} : oneWayPickupCity}
                                            isOptionDisabled={(pickUpOptions: any) => pickUpOptions.isDisabled}
                                            theme={(theme) => ({...theme, borderRadius: 4, colors: {...theme.colors, primary25: '#ddd', primary: '#fbb040',}
                                            })}/>
                                </div>
                                <div className={styles.onewayReturnDropdown}>
                                    <Select options={oneWaySelectOptionsReturn} isDisabled={(oneWayPickupCity.value).length <= 0}
                                            onChange={(e) => {handleOneWayCityChange(e, 'return')}}
                                            value={oneWayReturnCity.value === '' ? {value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true} : oneWayReturnCity}
                                            isOptionDisabled={(returnOptions: any) => returnOptions.isDisabled} theme={(theme) => ({
                                        ...theme, borderRadius: 4,
                                        colors: {...theme.colors, primary25: '#ddd', primary: '#fbb040',}
                                    })}/>
                                </div>
                            </div>}
                        <div className='row mx-0 px-0'>
                            <ThemeProvider theme={datePickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language} localeText={language === 'en' ? enUS.components.MuiLocalizationProvider.defaultProps.localeText : fiFI.components.MuiLocalizationProvider.defaultProps.localeText}>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation &&
                                            <img width="20" height="20" className='dateIcons' onClick={() => {setPickupDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice ?
                                            <MobileDatePicker
                                                disabled={false}
                                                open={isPickupDatePickerOpen}
                                                onAccept={() => {
                                                    setPickupDatePickerOpen(false);
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        setPickupTimePickerOpen(true);
                                                    }
                                                }}
                                                onOpen={() => {{setPickupDatePickerOpen(true)}}}
                                                onClose={() => {
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        setPickupTimePickerOpen(true);
                                                        setPickupDatePickerOpen(false);
                                                    } else {
                                                        setPickupDatePickerOpen(false);
                                                    }
                                                }}
                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                closeOnSelect
                                                label={translateService.translate('vehicleSearch.pickupDate')}
                                                disablePast
                                                format="DD.MM.YYYY"
                                                value={pickupDate}
                                                onChange={(p: any) => {
                                                    if (p !== null) {
                                                        if (p.isBefore(moment().seconds(0).milliseconds(0))) {
                                                            let newPickupDate = moment().seconds(0).milliseconds(0);
                                                            let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                            if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                intervals++;
                                                            }
                                                            if(intervals === 4) {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                            } else {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                            }
                                                            setPickupError(null);
                                                            setPickupDate(newPickupDate);
                                                        } else {
                                                            let newPickupDate = moment(p).seconds(0).milliseconds(0);
                                                            let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                            if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                intervals++;
                                                            }
                                                            if(intervals === 4) {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                            } else {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                            }
                                                            setPickupDate(newPickupDate);
                                                            setPickupError(null);
                                                        }
                                                        if (p.isAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                            const newReturnDate = moment(p).seconds(0).milliseconds(0).add(2, "hours")
                                                            setReturnDate(newReturnDate);
                                                            setReturnError("Return time cannot be before Pickup time");
                                                        } else {

                                                            setReturnError(null); // Clear the error message
                                                        }
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'outlined',
                                                        error: !!pickupErrorMessage,
                                                        helperText: pickupErrorMessage
                                                    }
                                                }}
                                                onError={(error: any) => setPickupError(error)}
                                            />
                                            : <div onClick={() => {if (!isPickupDatePickerOpen) {setPickupDatePickerOpen(true)}}}>
                                                <DatePicker
                                                    disabled={false}
                                                    open={isPickupDatePickerOpen}
                                                    onAccept={() => {
                                                        setPickupDatePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setPickupTimePickerOpen(true);
                                                        }}}
                                                    onOpen={() => {setPickupDatePickerOpen(true)}}
                                                    onClose={() => {
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setPickupTimePickerOpen(true);
                                                            setPickupDatePickerOpen(false);
                                                        } else {
                                                            setPickupDatePickerOpen(false);
                                                        }
                                                    }}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                    closeOnSelect={false}
                                                    label={translateService.translate('vehicleSearch.pickupDate')}
                                                    disablePast
                                                    format="DD.MM.YYYY"
                                                    value={pickupDate}
                                                    onChange={(p: any) => {
                                                        if (p !== null) {
                                                            if (p.isBefore(moment().seconds(0).milliseconds(0))) {
                                                                let newPickupDate = moment().seconds(0).milliseconds(0);
                                                                let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                                if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                    intervals++;
                                                                }
                                                                if(intervals === 4) {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                                } else {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                                }
                                                                setPickupError(null);
                                                                setPickupDate(newPickupDate);
                                                            } else {
                                                                let newPickupDate = moment(p).seconds(0).milliseconds(0);
                                                                let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                                if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                    intervals++;
                                                                }
                                                                if(intervals === 4) {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                                } else {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                                }
                                                                setPickupDate(newPickupDate);
                                                                setPickupError(null);
                                                            }
                                                            if (p.isAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                                const newReturnDate = moment(p).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                                setReturnError("Return time cannot be before Pickup time");
                                                            } else {
                                                                // Clear the error message
                                                                setReturnError(null);
                                                            }
                                                        }
                                                    }}
                                                    slots={{openPickerIcon: MuiDateTimeIcons}}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!pickupErrorMessage,
                                                            helperText: pickupErrorMessage
                                                        },
                                                        field: {readOnly: true},
                                                        actionBar: {
                                                            actions: ["cancel", "accept"],
                                                        },
                                                        inputAdornment: {
                                                            disablePointerEvents: true
                                                        },
                                                        popper: {
                                                            popperOptions: {
                                                                placement: !props.modifyReservation ? "bottom" : "auto"
                                                            }
                                                        }
                                                    }}
                                                    onError={(error: any) => setPickupError(error)}
                                                />
                                            </div>
                                        }
                                    </div>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => {setPickupTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice ?
                                            <TimePicker
                                                desktopModeMediaQuery={'@media print'}
                                                closeOnSelect={true}
                                                open={isPickupTimePickerOpen}
                                                onAccept={() => {
                                                    setPickupTimePickerOpen(false);
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        setReturnDatePickerOpen(true);
                                                    }
                                                }}
                                                onOpen={() => {setPickupTimePickerOpen(true)}}
                                                onClose={() => {
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        setReturnDatePickerOpen(true);
                                                        setPickupTimePickerOpen(false);
                                                    } else {
                                                        setPickupTimePickerOpen(false);
                                                    }
                                                }}
                                                label={translateService.translate('vehicleSearch.pickupTime')}
                                                format={"HH:mm"}
                                                minutesStep={15}
                                                skipDisabled
                                                minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                disabled={false}
                                                onChange={(p) => {
                                                    if(p) {
                                                        const newPickupDate = moment(pickupDate).seconds(0).milliseconds(0).set({
                                                            hour: moment(p).get('hour'),
                                                            minute: moment(p).get('minute')
                                                        })
                                                        // Check if the new pickup time is before the return time
                                                        if (newPickupDate.isSameOrAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                            const newReturnDate = moment(newPickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                            setReturnDate(newReturnDate);
                                                            setPickupDate(newPickupDate);
                                                            setPickupError(null);
                                                            // Set an error message
//                                                                setReturnError("Return time connot be before Pickup time");
                                                        } else {
                                                            // Update the pickup time
                                                            setPickupDate(newPickupDate);
                                                            setPickupError(null);
                                                            setReturnError(null); // Clear the error message
                                                        }
                                                    }
                                                }}
                                                slotProps={{
                                                    field: {
                                                        readOnly: true
                                                    },
                                                    textField: {
                                                        variant: 'outlined',
                                                        error: !!pickupErrorMessage,
                                                        helperText: pickupErrorMessage
                                                    },
                                                    openPickerButton: {
                                                        color: 'primary',
                                                    }
                                                }}
                                                disableIgnoringDatePartForTimeValidation
                                                ampm={false}
                                            />
                                            : <div onClick={() => {if (!isPickupTimePickerOpen) {setPickupTimePickerOpen(true)}}}>
                                                <DesktopTimePicker
                                                    closeOnSelect={true}
                                                    open={isPickupTimePickerOpen}
                                                    onAccept={() => {
                                                        setPickupTimePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setReturnDatePickerOpen(true);
                                                        }
                                                    }}
                                                    onOpen={() => {
                                                        setPickupTimePickerOpen(true);
                                                    }}
                                                    onClose={() => {
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setReturnDatePickerOpen(true);
                                                            setPickupTimePickerOpen(false);
                                                        } else {
                                                            setPickupTimePickerOpen(false);
                                                        }
                                                    }}
                                                    label={translateService.translate('vehicleSearch.pickupTime')}
                                                    format={"HH:mm"}
                                                    minutesStep={15}
                                                    skipDisabled
                                                    minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                    value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                    disabled={false}
                                                    onChange={(p) => {
                                                        if(p) {
                                                            const newPickupDate = moment(pickupDate).seconds(0).milliseconds(0).set({
                                                                hour: moment(p).get('hour'),
                                                                minute: moment(p).get('minute')
                                                            })
                                                            // Check if the new pickup time is before the return time
                                                            if (newPickupDate.isSameOrAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                                const newReturnDate = moment(newPickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                setPickupDate(newPickupDate);
                                                                setReturnDate(newReturnDate);
                                                                setPickupError(null);
                                                            } else {
                                                                setPickupDate(newPickupDate);
                                                                setPickupError(null);
                                                                setReturnError(null);
                                                            }
                                                        }
                                                    }}
                                                    slots={{ openPickerIcon: MuiDateTimeIcons}}
                                                    slotProps={{
                                                        field: {
                                                            readOnly: true
                                                        },
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!pickupErrorMessage,
                                                            helperText: pickupErrorMessage
                                                        },
                                                        actionBar: {
                                                            actions: ["cancel", "accept"],
                                                        },
                                                        inputAdornment: {
                                                            disablePointerEvents: true
                                                        },
                                                    }}
                                                    disableIgnoringDatePartForTimeValidation
                                                    ampm={false}
                                                />
                                        </div>
                                        }
                                    </div>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice ?
                                            <MobileDatePicker
                                                disabled={false}
                                                open={isReturnDatePickerOpen}
                                                onOpen={() =>{
                                                    setReturnDatePickerOpen(true);
                                                }}
                                                onAccept={() => {
                                                    setReturnDatePickerOpen(false);
                                                    if(!searchParameters?.cookie && !selectedALL) {
                                                        setReturnTimePickerOpen(true);
                                                    }
                                                }}
                                                onClose={() => {
                                                    if(!searchParameters?.cookie && !selectedALL) {
                                                        setReturnTimePickerOpen(true);
                                                        setReturnDatePickerOpen(false);
                                                    } else {
                                                        setReturnDatePickerOpen(false);
                                                    }
                                                }}
                                                closeOnSelect={true}
                                                disablePast
                                                format="DD.MM.YYYY"
                                                label={translateService.translate('vehicleSearch.returnDate')}
                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                value={returnDate}
                                                onChange={(p: any) => {
                                                    if (p !== null) {
                                                        if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                            const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                            setReturnDate(newReturnDate);
                                                        } else {
                                                            // Update the pickup time
                                                            setReturnDate(p);
                                                            setReturnError(null); // Clear the error message
                                                        }
                                                        if (showDefaultResults) {changeShowDefaultResults(false)}
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'outlined',
                                                        error: !!returnErrorMessage,
                                                        helperText: returnErrorMessage
                                                    }
                                                }}
                                                onError={(error) => setReturnError(error)}
                                            />
                                            : <div onClick={() => {if (!isReturnDatePickerOpen) {setReturnDatePickerOpen(true)}}}>
                                                <DatePicker
                                                    disabled={false}
                                                    open={isReturnDatePickerOpen}
                                                    onOpen={() =>{
                                                        setReturnDatePickerOpen(true);
                                                    }}
                                                    onAccept={() => {
                                                        setReturnDatePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL) {
                                                            setReturnTimePickerOpen(true);
                                                        }
                                                    }}
                                                    onClose={() => {
                                                        if(!searchParameters?.cookie && !selectedALL) {
                                                            setReturnTimePickerOpen(true);
                                                            setReturnDatePickerOpen(false);
                                                        } else {
                                                            setReturnDatePickerOpen(false);
                                                        }
                                                    }}
                                                    closeOnSelect={false}
                                                    disablePast
                                                    format="DD.MM.YYYY"
                                                    label={translateService.translate('vehicleSearch.returnDate')}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                    minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                    maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                    value={returnDate}
                                                    onChange={(p: any) => {
                                                        if (p !== null) {
                                                            if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                                const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                            } else {
                                                                // Update the pickup time
                                                                setReturnDate(p);
                                                                setReturnError(null); // Clear the error message
                                                            }
                                                            if (showDefaultResults) {changeShowDefaultResults(false)}
                                                        }
                                                    }}
                                                    slots={{openPickerIcon: MuiDateTimeIcons}}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!returnErrorMessage,
                                                            helperText: returnErrorMessage
                                                        },
                                                        field: {readOnly: true},
                                                        actionBar: {
                                                            actions: ["cancel", "accept"],
                                                        },
                                                        popper: {
                                                            popperOptions: {
                                                                placement: !props.modifyReservation ? "bottom" : "auto"
                                                            }
                                                        },
                                                        inputAdornment: {
                                                            disablePointerEvents: true
                                                        },
                                                    }}
                                                    onError={(error) => setReturnError(error)}
                                                />
                                        </div>
                                        }
                                    </div>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice?
                                            <TimePicker
                                                desktopModeMediaQuery={'@media print'}
                                                onOpen={() => {setReturnTimePickerOpen(true)}}
                                                open={isReturnTimePickerOpen}
                                                onAccept={() => {
                                                    setReturnDatePickerOpen(false);
                                                    setReturnTimePickerOpen(false);
                                                    setSelectedAll(true);
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        document.getElementById('search-button')?.focus();
                                                    }
                                                }}
                                                onClose={() => {
                                                    setSelectedAll(true);
                                                    setReturnTimePickerOpen(false);
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        document.getElementById('search-button')?.focus()
                                                    }}}
                                                closeOnSelect={true}
                                                label={translateService.translate('vehicleSearch.returnTime')}
                                                format={"HH:mm"}
                                                minutesStep={15}
                                                skipDisabled
                                                minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                value={moment(returnDate).seconds(0).milliseconds(0)}
                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                disabled={false}
                                                onChange={(p: any) => {
                                                    if (p !== null) {
                                                        if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                            const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                            setReturnDate(newReturnDate);
                                                        } else {
                                                            const newReturnDate = moment(returnDate).seconds(0).milliseconds(0).set({
                                                                hour: moment(p).get('hour'),
                                                                minute: moment(p).get('minute')
                                                            })
                                                            setReturnDate(newReturnDate);
                                                            setReturnError(null);
                                                        }
                                                        if (showDefaultResults) {
                                                            changeShowDefaultResults(false);
                                                        }
                                                    }
                                                }}
                                                slotProps={{
                                                    field: {
                                                        readOnly: true
                                                    },
                                                    textField: {
                                                        variant: 'outlined',
                                                        error: !!returnErrorMessage,
                                                        helperText: returnErrorMessage,
                                                    },
                                                    openPickerButton: {
                                                        color: 'primary',
                                                    }
                                                }}
                                                disableIgnoringDatePartForTimeValidation
                                                ampm={false}
                                            />
                                            : <div onClick={() => {if (!isReturnTimePickerOpen) {setReturnTimePickerOpen(true)}}}>
                                                <DesktopTimePicker
                                                    onOpen={() => {
                                                        setReturnTimePickerOpen(true);
                                                    }}
                                                    open={isReturnTimePickerOpen}
                                                    onAccept={() => {
                                                        setReturnDatePickerOpen(false);
                                                        setReturnTimePickerOpen(false);
                                                        setSelectedAll(true);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            document.getElementById('search-button')?.focus();
                                                        }
                                                    }}
                                                    onClose={() => {
                                                        setSelectedAll(true);
                                                        setReturnTimePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            document.getElementById('search-button')?.focus();
                                                        }}}
                                                    closeOnSelect={true}
                                                    label={translateService.translate('vehicleSearch.returnTime')}
                                                    format={"HH:mm"}
                                                    minutesStep={15}
                                                    skipDisabled
                                                    minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                    value={moment(returnDate).seconds(0).milliseconds(0)}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                    disabled={false}
                                                    onChange={(p: any) => {
                                                        if (p !== null) {
                                                            if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                                const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                            } else {
                                                                const newReturnDate = moment(returnDate).seconds(0).milliseconds(0).set({
                                                                    hour: moment(p).get('hour'),
                                                                    minute: moment(p).get('minute')
                                                                })
                                                                setReturnDate(newReturnDate);
                                                                setReturnError(null);
                                                            }
                                                            if (showDefaultResults) {changeShowDefaultResults(false)}
                                                        }
                                                    }}
                                                    slots={{ openPickerIcon: MuiDateTimeIcons }}
                                                    slotProps={{
                                                        field: {
                                                            readOnly: true
                                                        },
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!returnErrorMessage,
                                                            helperText: returnErrorMessage,
                                                        },
                                                        actionBar: {
                                                            actions: ["cancel", "accept"],
                                                        },
                                                        inputAdornment: {
                                                            disablePointerEvents: true
                                                        },
                                                    }}
                                                    disableIgnoringDatePartForTimeValidation
                                                    ampm={false}
                                                />
                                        </div>
                                        }
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                        <button
                            type="submit" autoFocus
                            disabled={(API_VERSION !== 'mock' || appContext === 'production') && ( disableButton || (props.modifyReservation ?
                                (searchParameters?.returnCity ? searchParameters?.returnCity.length < 1 : true) : (oneWayReturnCity.value).length === 0))}
                            className="btn btn-primary btn-outline-primary w-100 font-weight-bold text-uppercase"
                            style={{marginTop: "10px"}}
                            onClick={(e) => {
                                e.button === 0 && e.preventDefault(); // prevent double click
                                sendGaEvent('searchOneWayCars', 'oneWaySearchPressed', '', 0);
                                handleSearchChanges();
                                const startDate = pickupDate;
                                const startTime = pickupDate.clone().format("HH:mm");
                                const endDate = returnDate;
                                const endTime = returnDate.clone().format("HH:mm");

                                if (!checkDateTimeValues(startDate, endDate)) return;

                                if (API_VERSION === 'mock' && appContext !== 'production') {
                                    props.setSearchParams((params: ISearchParameters) => {
                                        return {
                                            ...params, startDate: startDate, startTime: startTime, endDate: endDate, endTime: endTime,
                                            km: 100, showStart: 0, showAmount: 10, pickupDate: pickupDate, returnDate: returnDate, pickupCity: oneWayPickupCity.value,
                                            returnCity: oneWayReturnCity.value, carId: !props.modifyReservation ? null : params.carId
                                        };
                                    });
                                }
                                else {
                                    if (!props.modifyReservation && (oneWayPickupCity.value).length && (oneWayReturnCity.value).length) {
                                        props.setSearchParams((params: ISearchParameters) => {
                                            return {
                                                ...params, startDate: startDate, startTime: startTime, endDate: endDate,
                                                endTime: endTime, km: 100, showStart: 0, showAmount: 10,
                                                pickupDate: pickupDate, returnDate: returnDate, pickupCity: oneWayPickupCity.value,
                                                returnCity: oneWayReturnCity.value,
                                                carId: !props.modifyReservation ? null : params.carId,
                                                cid: user?.id ?? customerBehaviorData.cid, utm_campaign: customerBehaviorData.utm_campaign,
                                                utm_source: customerBehaviorData.utm_source, utm_term: customerBehaviorData.utm_term,
                                                utm_medium: customerBehaviorData.utm_medium, utm_content: customerBehaviorData.utm_content,
                                                gclid: customerBehaviorData.gclid, cookie: r24CookieVal
                                            };});
                                    } else {
                                        props.setSearchParams((params: ISearchParameters) => {
                                            return {
                                                ...params, startDate: startDate, startTime: startTime, endDate: endDate, endTime: endTime,
                                                pickupDate: pickupDate, returnDate: returnDate, carId: !props.modifyReservation ? null : params.carId,
                                                cid: user?.id ?? customerBehaviorData.cid, utm_campaign: customerBehaviorData.utm_campaign,
                                                utm_source: customerBehaviorData.utm_source, utm_term: customerBehaviorData.utm_term,
                                                utm_medium: customerBehaviorData.utm_medium, utm_content: customerBehaviorData.utm_content,
                                                gclid: customerBehaviorData.gclid, cookie: r24CookieVal
                                            };
                                        });}
                                }
                                props.scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                        >
                            {!props.modifyReservation ?
                                (oneWayPickupCity.value === '' || oneWayReturnCity.value === '') ?
                                    (oneWayPickupCity.value === '' ? translateService.translate('vehicleSearch.selectPickupCity')
                                        : translateService.translate('vehicleSearch.selectReturnCity'))
                                    : translateService.translate('vehicleSearch.find')
                                : translateService.translate('vehicleSearch1.checkAvailability')}
                        </button>
                    </form>
                    :
                    <form>
                        {!props.modifyReservation &&
                            <div
                                className="position-relative mt-2">
                                <label id="searchbox-label"
                                       className={isLocationEmpty ? 'searchbox-label--empty px-1' : "px-1 searchbox-label"}
                                       data-shrink="true"
                                       htmlFor="location">{translateService.translate('vehicleSearch.enterCity')}</label>
                                <div className="position-relative" onKeyDown={(e) => {
                                    if(e.code === 'Enter') { // handle 'enter' click to prevent default vehicle search on page
                                        e.preventDefault();
                                        if (!(!location && (!searchParameters?.location || !searchParameters?.location.formatted_address) && !props.modifyReservation) || disableButton ) {
                                            document.getElementById("search-button")?.focus();
                                        }}}}>
                                    <div className="position-absolute" style={{top: '6px', left: "10px"}}>
                                        <img
                                            src={rootUrl+'/assets/icons/new/reservation-details/behavioural/icon-magnifying-glass.svg'}
                                            alt="icon-magnifying-glass.svg" loading="lazy"
                                            width="14px" height="14px"/>
                                    </div>
                                    <Autocomplete
                                        ref={autoCompleteRef}
                                        id="google-search-location"
                                        className="w-100 searchbox-input"
                                        placeholder={translateService.translate('vehicleSearch.search')}
                                        defaultValue={getDefaultLocationValue()}
                                        onPlaceSelected={(place: google.maps.places.PlaceResult) => {
                                            if(place.geometry === undefined || place.geometry === null) {
                                                // if user does not click from suggestions, no geolocation data will be saved -->
                                                // last search will be used + ref will be use dto reset this field to last search data
                                                return;
                                            }
                                            setLat(place.geometry.location.lat());
                                            setLng(place.geometry.location.lng());
                                            setLocation(place)
                                            setIsAddressSelected(true);
                                            handleSearchChanges();
                                            setUserLocationSearchString(place.formatted_address ? place.formatted_address : '');
                                        }}
                                        options={{
                                            types: ['geocode', 'establishment'],
                                            componentRestrictions: { country: "fi" }
                                        }}
                                        /*onChange={(e) => {if (e.currentTarget.value) {
                                            setUserLocationSearchString(e.currentTarget.value); // for vehicle search analytics table
                                        }}}*/
                                    />
                                </div>
                            </div>}
                        <div className='row mx-0 px-0'>
                            <ThemeProvider theme={datePickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language} localeText={language === 'en' ? enUS.components.MuiLocalizationProvider.defaultProps.localeText : fiFI.components.MuiLocalizationProvider.defaultProps.localeText}>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setPickupDatePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice ?
                                            <MobileDatePicker
                                                disabled={!isAddressSelected}
                                                open={isPickupDatePickerOpen}
                                                onAccept={() => {
                                                    setPickupDatePickerOpen(false);
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        setPickupTimePickerOpen(true);
                                                    }
                                                }}
                                                onOpen={() => {setPickupDatePickerOpen(true)}}
                                                onClose={() => {
                                                    if(!searchParameters?.cookie && !selectedALL){
                                                        setPickupTimePickerOpen(true);
                                                        setPickupDatePickerOpen(false);
                                                    } else {
                                                        setPickupDatePickerOpen(false);
                                                    }
                                                }}
                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                closeOnSelect
                                                label={translateService.translate('vehicleSearch.pickupDate')}
                                                disablePast
                                                format="DD.MM.YYYY"
                                                value={pickupDate}
                                                onChange={(p: any) => {
                                                    if (p !== null) {
                                                        if (p.isBefore(moment().seconds(0).milliseconds(0))) {
                                                            let newPickupDate = moment().seconds(0).milliseconds(0);
                                                            let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                            if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                intervals++;
                                                            }
                                                            if(intervals === 4) {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                            } else {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                            }
                                                            setPickupDate(newPickupDate);
                                                            setPickupError(null);
                                                        } else {
                                                            let newPickupDate = moment(p).seconds(0).milliseconds(0);
                                                            let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                            if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                intervals++;
                                                            }
                                                            if(intervals === 4) {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                            } else {
                                                                newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                            }
                                                            setPickupDate(newPickupDate);
                                                            setPickupError(null);
                                                        }
                                                        if (p.isAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                            const newReturnDate = moment(p).seconds(0).milliseconds(0).add(2, "hours")
                                                            setReturnDate(newReturnDate);
                                                            setReturnError("Return time cannot be before Pickup time");
                                                        } else {
                                                            // Clear the error message
                                                            setReturnError(null);
                                                        }
                                                    }
                                                    handleSearchChanges();
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'outlined',
                                                        error: !!pickupErrorMessage,
                                                        helperText: pickupErrorMessage
                                                    }}}
                                                onError={(error: any) => setPickupError(error)}
                                            />
                                        : <div onClick={() => {if (!isPickupDatePickerOpen && isAddressSelected) {setPickupDatePickerOpen(true)}}}>
                                                <DatePicker
                                                    disabled={!isAddressSelected}
                                                    open={isPickupDatePickerOpen}
                                                    onAccept={() => {
                                                        setPickupDatePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setPickupTimePickerOpen(true);
                                                        }
                                                    }}
                                                    onOpen={() => {
                                                        setPickupDatePickerOpen(true);
                                                    }}
                                                    onClose={() => {
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setPickupTimePickerOpen(true);
                                                            setPickupDatePickerOpen(false);
                                                        } else {
                                                            setPickupDatePickerOpen(false);
                                                        }
                                                    }}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                    closeOnSelect={false}
                                                    label={translateService.translate('vehicleSearch.pickupDate')}
                                                    disablePast
                                                    format="DD.MM.YYYY"
                                                    value={pickupDate}
                                                    onChange={(p: any) => {
                                                        if (p !== null) {
                                                            if (p.isBefore(moment().seconds(0).milliseconds(0))) {
                                                                let newPickupDate = moment().seconds(0).milliseconds(0);
                                                                let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                                if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                    intervals++;
                                                                }
                                                                if(intervals === 4) {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                                } else {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                                }
                                                                setPickupDate(newPickupDate);
                                                                setPickupError(null);
                                                            } else {
                                                                let newPickupDate = moment(p).seconds(0).milliseconds(0);
                                                                let intervals = Math.floor(moment(newPickupDate).minutes() / 15);
                                                                if (moment(newPickupDate).minutes() % 15 !== 0) {
                                                                    intervals++;
                                                                }
                                                                if(intervals === 4) {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).add(1, 'hours');
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(0);
                                                                } else {
                                                                    newPickupDate = moment(newPickupDate).seconds(0).milliseconds(0).minutes(intervals * 15);
                                                                }
                                                                setPickupDate(newPickupDate);
                                                                setPickupError(null);
                                                            }
                                                            if (p.isAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                                const newReturnDate = moment(p).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                                setReturnError("Return time cannot be before Pickup time");
                                                            } else {
                                                                setReturnError(null); // Clear the error message
                                                            }
                                                        }
                                                        handleSearchChanges();
                                                    }}
                                                    slots={{
                                                        openPickerIcon: MuiDateTimeIcons,
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!pickupErrorMessage,
                                                            helperText: pickupErrorMessage
                                                        },
                                                        field: {readOnly: true},
                                                        actionBar: {
                                                            actions: ["cancel", "accept"],
                                                        },
                                                        inputAdornment: {
                                                            disablePointerEvents: true
                                                        },
                                                        popper: {
                                                            popperOptions: {
                                                                placement: !props.modifyReservation ? "bottom" : "auto"
                                                            }
                                                        }
                                                    }}
                                                    onError={(error: any) => setPickupError(error)}
                                                />
                                            </div>
                                            }
                                    </div>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setPickupTimePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice?
                                                <TimePicker
                                                    desktopModeMediaQuery={'@media print'}
                                                    closeOnSelect={true}
                                                    open={isPickupTimePickerOpen}
                                                    onAccept={() => {
                                                        setPickupTimePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setReturnDatePickerOpen(true);
                                                        }
                                                    }}
                                                    onOpen={() => {setPickupTimePickerOpen(true)}}
                                                    onClose={() => {
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            setReturnDatePickerOpen(true);
                                                            setPickupTimePickerOpen(false);
                                                        } else {
                                                            setPickupTimePickerOpen(false);
                                                        }
                                                    }}
                                                    label={translateService.translate('vehicleSearch.pickupTime')}
                                                    format={"HH:mm"}
                                                    minutesStep={15}
                                                    skipDisabled
                                                    minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                    value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                    disabled={!isAddressSelected}
                                                    onChange={(p) => {
                                                        if(p) {
                                                            const newPickupDate = moment(pickupDate).seconds(0).milliseconds(0).set({
                                                                hour: moment(p).get('hour'),
                                                                minute: moment(p).get('minute')
                                                            })
                                                            handleSearchChanges();
                                                            // Check if the new pickup time is before the return time
                                                            if (newPickupDate.isSameOrAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                                const newReturnDate = moment(newPickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                                setPickupDate(newPickupDate);
                                                                setPickupError(null);
                                                            } else {
                                                                setPickupDate(newPickupDate);
                                                                setPickupError(null);
                                                                setReturnError(null);
                                                            }
                                                        }
                                                    }}
                                                    slotProps={{
                                                        field: {
                                                            readOnly: true
                                                        },
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!pickupErrorMessage,
                                                            helperText: pickupErrorMessage
                                                        },
                                                        openPickerButton: {
                                                            color: 'primary',
                                                        }
                                                    }}
                                                    disableIgnoringDatePartForTimeValidation
                                                    ampm={false}
                                                />
                                                :
                                                <div onClick={() => {if (!isPickupTimePickerOpen && isAddressSelected) {setPickupTimePickerOpen(true)}}}>
                                                    <DesktopTimePicker
                                                        closeOnSelect={true}
                                                        open={isPickupTimePickerOpen}
                                                        onAccept={() => {
                                                            setPickupTimePickerOpen(false);
                                                            if(!searchParameters?.cookie && !selectedALL){
                                                                setReturnDatePickerOpen(true);
                                                            }
                                                        }}
                                                        onOpen={() => {setPickupTimePickerOpen(true)}}
                                                        onClose={() => {
                                                            if(!searchParameters?.cookie && !selectedALL){
                                                                setReturnDatePickerOpen(true);
                                                                setPickupTimePickerOpen(false);
                                                            } else {
                                                                setPickupTimePickerOpen(false);
                                                            }
                                                        }}
                                                        label={translateService.translate('vehicleSearch.pickupTime')}
                                                        format={"HH:mm"}
                                                        minutesStep={15}
                                                        skipDisabled
                                                        minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                        value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                        disabled={!isAddressSelected}
                                                        onChange={(p) => {
                                                            if(p) {
                                                                const newPickupDate = moment(pickupDate).seconds(0).milliseconds(0).set({
                                                                    hour: moment(p).get('hour'),
                                                                    minute: moment(p).get('minute')
                                                                })
                                                                handleSearchChanges();
                                                                // Check if the new pickup time is before the return time
                                                                if (newPickupDate.isSameOrAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
                                                                    const newReturnDate = moment(newPickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                    setReturnDate(newReturnDate);
                                                                    setPickupDate(newPickupDate);
                                                                    setPickupError(null);
                                                                } else {
                                                                    // Update the pickup time
                                                                    setPickupDate(newPickupDate);
                                                                    setPickupError(null);
                                                                    setReturnError(null); // Clear the error message
                                                                }
                                                            }
                                                        }}
                                                        slots={{ openPickerIcon: MuiDateTimeIcons }}
                                                        slotProps={{
                                                            field: {
                                                                readOnly: true
                                                            },
                                                            textField: {
                                                                variant: 'outlined',
                                                                error: !!pickupErrorMessage,
                                                                helperText: pickupErrorMessage
                                                            },
                                                            actionBar: {
                                                                actions: ["cancel", "accept"],
                                                            },
                                                            inputAdornment: {
                                                                disablePointerEvents: true
                                                            },
                                                        }}
                                                        disableIgnoringDatePartForTimeValidation
                                                        ampm={false}
                                                    />
                                                </div>
                                        }
                                    </div>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                        {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setReturnDatePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice ?
                                                <MobileDatePicker
                                                    disabled={!isAddressSelected}
                                                    open={isReturnDatePickerOpen}
                                                    onOpen={() =>{setReturnDatePickerOpen(true)}}
                                                    onAccept={() => {
                                                        setReturnDatePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL) {
                                                            setReturnTimePickerOpen(true);
                                                        }
                                                    }}
                                                    onClose={() => {
                                                        if(!searchParameters?.cookie && !selectedALL) {
                                                            setReturnTimePickerOpen(true);
                                                            setReturnDatePickerOpen(false);
                                                        } else {
                                                            setReturnDatePickerOpen(false);
                                                        }
                                                    }}
                                                    closeOnSelect={true}
                                                    disablePast
                                                    format="DD.MM.YYYY"
                                                    label={translateService.translate('vehicleSearch.returnDate')}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                    minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                    maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                    value={returnDate}
                                                    onChange={(p: any) => {
                                                        if (p !== null) {
                                                            if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                                const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                            } else {
                                                                // Update the pickup time
                                                                setReturnDate(p);
                                                                setReturnError(null); // Clear the error message
                                                            }
                                                            handleSearchChanges();
                                                        }}}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!returnErrorMessage,
                                                            helperText: returnErrorMessage
                                                        }
                                                    }}
                                                    onError={(error) => setReturnError(error)}
                                                />
                                                : <div onClick={() => {if (!isReturnDatePickerOpen && isAddressSelected) {setReturnDatePickerOpen(true)}}}>
                                                    <DatePicker
                                                        disabled={!isAddressSelected}
                                                        open={isReturnDatePickerOpen}
                                                        onOpen={() =>{
                                                            setReturnDatePickerOpen(true);
                                                        }}
                                                        onAccept={() => {
                                                            setReturnDatePickerOpen(false);
                                                            if(!searchParameters?.cookie && !selectedALL) {
                                                                setReturnTimePickerOpen(true);
                                                            }
                                                        }}
                                                        onClose={() => {
                                                            if(!searchParameters?.cookie && !selectedALL) {
                                                                setReturnTimePickerOpen(true);
                                                                setReturnDatePickerOpen(false);
                                                            } else {
                                                                setReturnDatePickerOpen(false);
                                                            }
                                                          }}
                                                        closeOnSelect={false}
                                                        disablePast
                                                        format="DD.MM.YYYY"
                                                        label={translateService.translate('vehicleSearch.returnDate')}
                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                        minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                        maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                        value={returnDate}
                                                        onChange={(p: any) => {
                                                            if (p !== null) {
                                                                if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                                    const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                    setReturnDate(newReturnDate);
                                                                } else {
                                                                    // Update the pickup time
                                                                    setReturnDate(p);
                                                                    setReturnError(null);// Clear the error message
                                                                }
                                                                handleSearchChanges();
                                                            }
                                                        }}
                                                        slots={{ openPickerIcon: MuiDateTimeIcons }}
                                                        slotProps={{
                                                            textField: {
                                                                variant: 'outlined',
                                                                error: !!returnErrorMessage,
                                                                helperText: returnErrorMessage
                                                            },
                                                            field: {readOnly: true},
                                                            actionBar: {
                                                                actions: ["cancel", "accept"],
                                                            },
                                                            popper: {
                                                                popperOptions: {
                                                                    placement: !props.modifyReservation ? "bottom" : "auto"
                                                                }
                                                            },
                                                            inputAdornment: {
                                                                disablePointerEvents: true
                                                            },
                                                        }}
                                                        onError={(error) => setReturnError(error)}
                                                    />
                                            </div>
                                        }
                                    </div>
                                    <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`} >
                                        {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setReturnTimePickerOpen(true)}}}
                                                                          src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                        { mobileDevice ?
                                                <TimePicker
                                                    desktopModeMediaQuery={'@media print'}
                                                    onOpen={() => {
                                                        setReturnTimePickerOpen(true);
                                                    }}
                                                    open={isReturnTimePickerOpen}
                                                    onAccept={() => {
                                                        setReturnDatePickerOpen(false);
                                                        setReturnTimePickerOpen(false);
                                                        setSelectedAll(true);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            document.getElementById('search-button')?.focus();
                                                        }
                                                    }}
                                                    onClose={() => {
                                                        setSelectedAll(true);
                                                        setReturnTimePickerOpen(false);
                                                        if(!searchParameters?.cookie && !selectedALL){
                                                            document.getElementById('search-button')?.focus();
                                                        }
                                                    }}
                                                    closeOnSelect={true}
                                                    label={translateService.translate('vehicleSearch.returnTime')}
                                                    format={"HH:mm"}
                                                    minutesStep={15}
                                                    skipDisabled
                                                    minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                    value={moment(returnDate).seconds(0).milliseconds(0)}
                                                    className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                    disabled={!isAddressSelected}
                                                    onChange={(p: any) => {
                                                        if (p !== null) {
                                                            if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                                const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                setReturnDate(newReturnDate);
                                                            } else {
                                                                const newReturnDate = moment(returnDate).seconds(0).milliseconds(0).set({
                                                                    hour: moment(p).get('hour'),
                                                                    minute: moment(p).get('minute')
                                                                })
                                                                setReturnDate(newReturnDate);
                                                                setReturnError(null);
                                                            }
                                                        }
                                                    }}
                                                    slotProps={{
                                                        field: {
                                                            readOnly: true
                                                        },
                                                        textField: {
                                                            variant: 'outlined',
                                                            error: !!returnErrorMessage,
                                                            helperText: returnErrorMessage,
                                                        },
                                                        openPickerButton: {
                                                            color: 'primary',
                                                        }
                                                    }}
                                                    disableIgnoringDatePartForTimeValidation
                                                    ampm={false}
                                                />
                                                : <div onClick={() => {if (!isReturnTimePickerOpen && isAddressSelected) {setReturnTimePickerOpen(true)}}}>
                                                    <DesktopTimePicker
                                                        onOpen={() => {setReturnTimePickerOpen(true)}}
                                                        open={isReturnTimePickerOpen}
                                                        onAccept={() => {
                                                            setReturnDatePickerOpen(false);
                                                            setReturnTimePickerOpen(false);
                                                            setSelectedAll(true);
                                                            if(!searchParameters?.cookie && !selectedALL){
                                                                document.getElementById('search-button')?.focus();
                                                            }
                                                        }}
                                                        onClose={() => {
                                                            setSelectedAll(true);
                                                            setReturnTimePickerOpen(false);
                                                            if(!searchParameters?.cookie && !selectedALL){
                                                                document.getElementById('search-button')?.focus();
                                                            }
                                                        }}
                                                        closeOnSelect={true}
                                                        label={translateService.translate('vehicleSearch.returnTime')}
                                                        format={"HH:mm"}
                                                        minutesStep={15}
                                                        skipDisabled
                                                        minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                        value={moment(returnDate).seconds(0).milliseconds(0)}
                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                        disabled={!isAddressSelected}
                                                        onChange={(p: any) => {
                                                            if (p !== null) {
                                                                if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
                                                                    const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")
                                                                    setReturnDate(newReturnDate);
                                                                } else {
                                                                    const newReturnDate = moment(returnDate).seconds(0).milliseconds(0).set({
                                                                        hour: moment(p).get('hour'),
                                                                        minute: moment(p).get('minute')
                                                                    })
                                                                    setReturnDate(newReturnDate);
                                                                    setReturnError(null);
                                                                }
                                                            }
                                                        }}
                                                        slots={{ openPickerIcon: MuiDateTimeIcons }}
                                                        slotProps={{
                                                            field: {
                                                                readOnly: true
                                                            },
                                                            textField: {
                                                                variant: 'outlined',
                                                                error: !!returnErrorMessage,
                                                                helperText: returnErrorMessage,
                                                            },
                                                            actionBar: {
                                                                actions: ["cancel", "accept"],
                                                            },
                                                            inputAdornment: {
                                                                disablePointerEvents: true
                                                            },
                                                        }}
                                                        disableIgnoringDatePartForTimeValidation
                                                        ampm={false}
                                                    />
                                                </div>
                                        }
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                        <button
                            id="search-button"
                            type="submit" autoFocus
                            disabled={(API_VERSION !== 'mock' || appContext === 'production') && ((!location && (!searchParameters?.location || !searchParameters?.location.formatted_address) && !props.modifyReservation) || disableButton )}
                            className="btn btn-primary btn-outline-primary w-100 font-weight-bold text-uppercase"
                            style={{marginTop: "10px"}}
                            onClick={(e) => {
                                e.button === 0 && e.preventDefault(); // prevent double click
                                sendGaEvent('searchCars', 'searchPressed', '', 0);
                                if ((autoCompleteRef?.current?.value || autoCompleteRef?.current?.value === '') && location?.formatted_address) {
                                    // note: this is used specifically for the <Autocomplete />, if component for searching changed --> this reset method may need a revisit
                                    autoCompleteRef.current.value = location?.formatted_address // set field back to location name used for current search
                                }

                                const startDate = pickupDate;
                                const startTime = pickupDate.clone().format("HH:mm");
                                const endDate = returnDate;
                                const endTime = returnDate.clone().format("HH:mm");
                                if (!checkDateTimeValues(startDate, endDate)) return;

                                if (location) {
                                    changeSearchParameters({
                                        ...searchParameters,
                                        lat: lat,
                                        lon: lng,
                                        location: location
                                    })
                                }
                                if (!hasPopupShown &&
                                    !loggedIn &&
                                    (moment().hour() >= 21 || moment().hour() < 8) &&
                                    (moment().isSame(pickupDate, 'day') || moment(pickupDate).isBefore(moment().add(1, 'day').hour(8).minute(0).second(0).millisecond(0)))) {
                                    setShowPopup(true);
                                    setHasPopupShown(true);
                                }

                                let saveLat = 0, saveLon = 0;
                                if (API_VERSION === 'mock' && appContext !== 'production') {
                                    props.setSearchParams((params: ISearchParameters) => {
                                        return {
                                            ...params,
                                            startDate: startDate, startTime: startTime, endDate: endDate,
                                            endTime: endTime, lat: saveLat, lon: saveLon, distance: 30000, km: 100,
                                            showStart: 0, showAmount: 10, discount: params.discount ?? user?.couponCode,
                                            pickupDate: pickupDate, returnDate: returnDate, location: location,
                                            carId: !props.modifyReservation ? null : params.carId,
                                            fuelType: props.modifyReservation ? params?.fuelType : undefined,
                                            vehicleType: props.modifyReservation ? params?.vehicleType : undefined,
                                            crossBorderArea: props.modifyReservation ? params?.crossBorderArea : undefined,
                                            transmission: props.modifyReservation ? params?.transmission : undefined,
                                            features: props.modifyReservation ? params?.features : undefined, pickupCity: undefined, returnCity: undefined,
                                            userPlaceSearch: userLocationSearchString
                                        };
                                    });
                                } else {
                                    if (!props.modifyReservation && location) {
                                        props.setSearchParams((params: ISearchParameters) => {
                                            return {
                                                ...params,
                                                startDate: startDate, startTime: startTime, endDate: endDate, endTime: endTime,
                                                lat: lat, lon: lng, distance: 30000,
                                                km: 100, showStart: 0, showAmount: 10, discount: params?.discount ?? user?.couponCode,
                                                pickupDate: pickupDate, returnDate: returnDate, location: location,
                                                carId: !props.modifyReservation ? null : params?.carId,
                                                fuelType: props.modifyReservation ? params?.fuelType : undefined,
                                                vehicleType: props.modifyReservation ? params?.vehicleType : undefined,
                                                crossBorderArea: props.modifyReservation ? params?.crossBorderArea : undefined,
                                                transmission: props.modifyReservation ? params?.transmission : undefined,
                                                features: props.modifyReservation ? params?.features : undefined, pickupCity: undefined, returnCity: undefined,
                                                cid: user?.id ?? customerBehaviorData.cid, utm_campaign: customerBehaviorData.utm_campaign,
                                                utm_source: customerBehaviorData.utm_source, utm_term: customerBehaviorData.utm_term,
                                                utm_medium: customerBehaviorData.utm_medium, utm_content: customerBehaviorData.utm_content,
                                                gclid: customerBehaviorData.gclid, cookie: r24CookieVal, userPlaceSearch: userLocationSearchString
                                            };
                                        });
                                    } else {
                                        props.setSearchParams((params: ISearchParameters) => {
                                            return {
                                                ...params,
                                                startDate: startDate,
                                                startTime: startTime,
                                                endDate: endDate,
                                                endTime: endTime,
                                                pickupDate: pickupDate,
                                                returnDate: returnDate,
                                                carId: !props.modifyReservation ? null : params.carId,
                                                fuelType: undefined, vehicleType: undefined, crossBorderArea: undefined,
                                                transmission: undefined, features: undefined, pickupCity: undefined, returnCity: undefined,
                                                cid: user?.id ?? customerBehaviorData.cid, utm_campaign: customerBehaviorData.utm_campaign,
                                                utm_source: customerBehaviorData.utm_source, utm_term: customerBehaviorData.utm_term,
                                                utm_medium: customerBehaviorData.utm_medium, utm_content: customerBehaviorData.utm_content,
                                                gclid: customerBehaviorData.gclid, cookie: r24CookieVal, userPlaceSearch: userLocationSearchString
                                            };
                                        });
                                    }
                                }
                                props.scrollRef?.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
                            }}>
                            {!props.modifyReservation ?
                                showDefaultResults && !location ? translateService.translate('vehicleSearch1.selectLocationAndDate') : translateService.translate('vehicleSearch.find')
                                : translateService.translate('vehicleSearch1.checkAvailability')}
                        </button>
                    </form>
                }
                {showPopup &&
                    <Dialog className='extension-modal' open={showPopup}>
                        <Card className={`card-body py-4`}>
                            <div className="text-center py-2">
                                <h2>{translateService.translate('vehicleSearch.hi')}</h2>
                                <span>{translateService.translate('vehicleSearch.OutOfRangeError1')}</span>
                                <br/>
                                <span>{translateService.translate('vehicleSearch.OutOfRangeError2')}</span>
                            </div>
                            <button className={'btn btn-primary btn-outline-primary mt-2 w-100'} onClick={() => setShowPopup(false)}>
                                <span>OK</span>
                            </button>
                        </Card>
                    </Dialog>}
            </Card>
        </>
    )
}
export default VehicleSearch;